import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Phone, MapPin, Plus, Trash2, Navigation, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Profile = () => {
  const { user, token, addAddress, deleteAddress, refreshUser } = useAuth();
  const [showAddressDialog, setShowAddressDialog] = useState(false);
  const [gettingLocationFor, setGettingLocationFor] = useState(null);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new_pass: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [newAddress, setNewAddress] = useState({
    title: '',
    address: '',
    district: '',
    city: '',
    is_default: false,
    latitude: null,
    longitude: null
  });
  const [gettingLocation, setGettingLocation] = useState(false);

  const getLocationForNew = () => {
    if (!navigator.geolocation) {
      toast.error('Tarayıcınız konum desteklemiyor');
      return;
    }
    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setNewAddress(prev => ({ ...prev, latitude: pos.coords.latitude, longitude: pos.coords.longitude }));
        toast.success('Konum alındı!');
        setGettingLocation(false);
      },
      () => {
        toast.error('Konum alınamadı. Lütfen konum iznini verin.');
        setGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const updateAddressLocation = (addressId) => {
    if (!navigator.geolocation) {
      toast.error('Tarayıcınız konum desteklemiyor');
      return;
    }
    setGettingLocationFor(addressId);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await axios.put(
            `${API}/user/address/${addressId}/location?lat=${pos.coords.latitude}&lon=${pos.coords.longitude}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          await refreshUser();
          toast.success('Konum güncellendi!');
        } catch {
          toast.error('Konum güncellenirken hata oluştu');
        }
        setGettingLocationFor(null);
      },
      () => {
        toast.error('Konum alınamadı. Lütfen konum iznini verin.');
        setGettingLocationFor(null);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleAddAddress = async () => {
    if (!newAddress.title || !newAddress.address || !newAddress.district || !newAddress.city) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }
    
    try {
      await addAddress(newAddress);
      toast.success('Adres eklendi');
      setShowAddressDialog(false);
      setNewAddress({ title: '', address: '', district: '', city: '', is_default: false, latitude: null, longitude: null });
    } catch {
      toast.error('Adres eklenirken hata oluştu');
    }
  };

  const handleDeleteAddress = async (addressId) => {
    try {
      await deleteAddress(addressId);
      toast.success('Adres silindi');
    } catch {
      toast.error('Adres silinirken hata oluştu');
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current) { toast.error('Mevcut sifrenizi girin'); return; }
    if (passwordData.new_pass.length < 6) { toast.error('Yeni sifre en az 6 karakter olmali'); return; }
    if (passwordData.new_pass !== passwordData.confirm) { toast.error('Sifreler eslesmiyor'); return; }
    setChangingPassword(true);
    try {
      await axios.put(`${API}/user/password?current_password=${encodeURIComponent(passwordData.current)}&new_password=${encodeURIComponent(passwordData.new_pass)}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sifre guncellendi!');
      setPasswordData({ current: '', new_pass: '', confirm: '' });
      setShowPasswordForm(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sifre guncellenemedi');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone pb-8">
      {/* Header */}
      <div className="bg-sand py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-charcoal mb-2">
              Profilim
            </h1>
            <p className="text-charcoal/60">
              Hesap bilgilerinizi ve adreslerinizi yönetin
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile Info */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="text-center mb-6">
                <div className="w-20 h-20 bg-basil rounded-full flex items-center justify-center mx-auto mb-4">
                  <User className="w-10 h-10 text-white" />
                </div>
                <h2 className="font-playfair text-xl font-semibold text-charcoal">
                  {user?.name}
                </h2>
                {user?.is_admin && (
                  <span className="inline-block px-3 py-1 bg-terracotta/10 text-terracotta text-xs font-medium rounded-full mt-2">
                    Admin
                  </span>
                )}
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-3 text-charcoal/70">
                  <Mail className="w-5 h-5" />
                  <span>{user?.email}</span>
                </div>
                <div className="flex items-center gap-3 text-charcoal/70">
                  <Phone className="w-5 h-5" />
                  <span>{user?.phone}</span>
                </div>
              </div>

              {/* Password Change */}
              <div className="mt-6 pt-4 border-t">
                {!showPasswordForm ? (
                  <button
                    onClick={() => setShowPasswordForm(true)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-stone-200 text-sm font-medium text-charcoal/60 hover:bg-stone-50 transition-colors"
                    data-testid="show-password-form-btn"
                  >
                    <Lock className="w-4 h-4" />
                    Sifre Degistir
                  </button>
                ) : (
                  <div className="space-y-3">
                    <div className="relative">
                      <Input
                        type={showPasswords ? 'text' : 'password'}
                        placeholder="Mevcut Sifre"
                        value={passwordData.current}
                        onChange={(e) => setPasswordData({ ...passwordData, current: e.target.value })}
                        data-testid="current-password-input"
                      />
                    </div>
                    <Input
                      type={showPasswords ? 'text' : 'password'}
                      placeholder="Yeni Sifre (min 6)"
                      value={passwordData.new_pass}
                      onChange={(e) => setPasswordData({ ...passwordData, new_pass: e.target.value })}
                      data-testid="new-password-profile-input"
                    />
                    <Input
                      type={showPasswords ? 'text' : 'password'}
                      placeholder="Yeni Sifre Tekrar"
                      value={passwordData.confirm}
                      onChange={(e) => setPasswordData({ ...passwordData, confirm: e.target.value })}
                      data-testid="confirm-password-profile-input"
                    />
                    <button onClick={() => setShowPasswords(!showPasswords)} className="text-xs text-charcoal/50 flex items-center gap-1">
                      {showPasswords ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                      {showPasswords ? 'Gizle' : 'Goster'}
                    </button>
                    <div className="flex gap-2">
                      <Button onClick={handleChangePassword} disabled={changingPassword} className="flex-1 bg-basil text-white hover:bg-basil/90 text-sm" data-testid="save-password-btn">
                        {changingPassword ? 'Kaydediliyor...' : 'Kaydet'}
                      </Button>
                      <Button variant="outline" onClick={() => { setShowPasswordForm(false); setPasswordData({ current: '', new_pass: '', confirm: '' }); }} className="text-sm">Iptal</Button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Addresses */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-basil" />
                  <h2 className="font-playfair text-xl font-semibold text-charcoal">
                    Adreslerim
                  </h2>
                </div>
                <Dialog open={showAddressDialog} onOpenChange={setShowAddressDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="rounded-full"
                      data-testid="profile-add-address-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Yeni Adres
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Yeni Adres Ekle</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Adres Basligi</Label>
                        <Input
                          placeholder="Orn: Ev, Is"
                          value={newAddress.title}
                          onChange={(e) => setNewAddress({ ...newAddress, title: e.target.value })}
                          className="mt-1"
                          data-testid="profile-address-title-input"
                        />
                      </div>
                      <div>
                        <Label>Adres</Label>
                        <Textarea
                          placeholder="Sokak, apartman, daire no"
                          value={newAddress.address}
                          onChange={(e) => setNewAddress({ ...newAddress, address: e.target.value })}
                          className="mt-1"
                          data-testid="profile-address-detail-input"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Ilce</Label>
                          <Input
                            placeholder="Ilce"
                            value={newAddress.district}
                            onChange={(e) => setNewAddress({ ...newAddress, district: e.target.value })}
                            className="mt-1"
                            data-testid="profile-address-district-input"
                          />
                        </div>
                        <div>
                          <Label>Sehir</Label>
                          <Input
                            placeholder="Sehir"
                            value={newAddress.city}
                            onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                            className="mt-1"
                            data-testid="profile-address-city-input"
                          />
                        </div>
                      </div>
                      <div>
                        <button
                          type="button"
                          onClick={getLocationForNew}
                          disabled={gettingLocation}
                          className={`w-full mt-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${newAddress.latitude ? 'border-green-500 bg-green-50 text-green-700' : 'border-stone-200 text-charcoal/60 hover:border-basil'}`}
                          data-testid="profile-get-location-btn"
                        >
                          <Navigation className="w-4 h-4" />
                          {gettingLocation ? 'Konum aliniyor...' : newAddress.latitude ? 'Konum Alindi' : 'Konumumu Al'}
                        </button>
                        {newAddress.latitude && (
                          <p className="text-xs text-green-600 mt-1 text-center">{newAddress.latitude.toFixed(5)}, {newAddress.longitude.toFixed(5)}</p>
                        )}
                      </div>
                      <Button 
                        onClick={handleAddAddress} 
                        className="w-full bg-basil text-white hover:bg-basil/90"
                        data-testid="profile-save-address-btn"
                      >
                        Adresi Kaydet
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              {user?.addresses?.length > 0 ? (
                <div className="space-y-4">
                  {user.addresses.map((addr) => (
                    <div
                      key={addr.id}
                      className="flex items-start justify-between p-4 rounded-xl border border-stone-200 hover:border-stone-300 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-charcoal">{addr.title}</span>
                          {addr.is_default && (
                            <span className="text-xs bg-basil/10 text-basil px-2 py-0.5 rounded-full">
                              Varsayilan
                            </span>
                          )}
                          {addr.latitude && addr.longitude ? (
                            <span className="text-xs bg-green-50 text-green-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                              <Navigation className="w-3 h-3" />
                              Konum Var
                            </span>
                          ) : (
                            <span className="text-xs bg-amber-50 text-amber-600 px-2 py-0.5 rounded-full">
                              Konum Yok
                            </span>
                          )}
                        </div>
                        <p className="text-charcoal/70 text-sm">{addr.address}</p>
                        <p className="text-charcoal/60 text-sm">{addr.district}, {addr.city}</p>
                        {!addr.latitude && (
                          <button
                            onClick={() => updateAddressLocation(addr.id)}
                            disabled={gettingLocationFor === addr.id}
                            className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-amber-600 hover:text-amber-700 transition-colors"
                            data-testid={`update-location-${addr.id}`}
                          >
                            <Navigation className="w-3.5 h-3.5" />
                            {gettingLocationFor === addr.id ? 'Konum aliniyor...' : 'Konum Ekle'}
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => handleDeleteAddress(addr.id)}
                        className="p-2 text-charcoal/40 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                        data-testid={`delete-address-${addr.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <MapPin className="w-12 h-12 text-charcoal/20 mx-auto mb-4" />
                  <p className="text-charcoal/60">Henuz kayitli adresiniz yok.</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
